import type { ReactNode } from 'react';

import type { SymbolKey } from '@/entities/symbol';

export type SymbolPreviewPresentation = 'default' | 'nested';

/**
 * Slot registry for the shared `SymbolsPreview` table.
 *
 * `SymbolsPreview` lives in `features/_shared` and may not depend on sibling
 * features. The selection controls (owned by `symbol-selector`) and the
 * factsheet links (owned by `factsheet`) are contributed through this registry
 * instead: each owning feature registers its renderer at app boot, and the
 * host resolves them by slot. Imports therefore flow only toward `_shared` —
 * the host never imports a contributor. See refactor.md §4.1 (registry/plugin).
 */
export type SymbolCheckboxRenderer = (
  symbol: SymbolKey,
  presentation: SymbolPreviewPresentation
) => ReactNode;
export type SelectAllRenderer = (symbols: SymbolKey[]) => ReactNode;
export type SymbolInfoRenderer = (code: string) => ReactNode;
export type SymbolTitleWrapper = (code: string, children: ReactNode) => ReactNode;

export interface SymbolPreviewSlots {
  checkbox?: SymbolCheckboxRenderer;
  selectAll?: SelectAllRenderer;
  info?: SymbolInfoRenderer;
  titleWrapper?: SymbolTitleWrapper;
}

interface SymbolPreviewRegistry {
  registerSelection(checkbox: SymbolCheckboxRenderer, selectAll: SelectAllRenderer): void;
  registerInfo(info: SymbolInfoRenderer, titleWrapper: SymbolTitleWrapper): void;
  getSlots(): Readonly<SymbolPreviewSlots>;
  reset(): void;
}

function createRegistry(): SymbolPreviewRegistry {
  let slots: SymbolPreviewSlots = {};
  return {
    registerSelection(checkbox, selectAll) {
      slots = { ...slots, checkbox, selectAll };
    },
    registerInfo(info, titleWrapper) {
      slots = { ...slots, info, titleWrapper };
    },
    getSlots() {
      return slots;
    },
    reset() {
      slots = {};
    }
  };
}

export const symbolPreviewRegistry = createRegistry();
